import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Menu } from 'lucide-react';
import { MENU_ITEMS } from '@/constants/menu';
import { Separator } from '@/components/ui/separator';
import { Logo } from './logo';
import { ActionButtons } from './action-buttons';
import {
  ExpandableMenuItem,
  isExpandableMenuItem,
  isSimpleMenuItem,
} from '@/types/menu';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { ProductHeaderData } from '@/types/product-pages';
import { cn } from '@/lib/utils';

const MOBILE_ACTION_PILL =
  'h-[38px] w-full max-w-[198px] whitespace-normal rounded-full px-3 py-0 text-center text-[clamp(9px,2.6vw,12px)] font-bold uppercase leading-tight tracking-[0.06em] text-white';

/*
 * grid em vez de flex: colunas iguais mesmo com textos de comprimentos
 * diferentes. Acima de 440px a linha para de crescer e encosta a direita,
 * junto com os links do menu.
 */
const MOBILE_ACTIONS_GRID =
  'ml-auto grid w-full max-w-[440px] grid-cols-2 items-stretch gap-3 px-4 pb-6 pt-4';

const MOBILE_ACTIONS = [
  {
    ...MENU_ITEMS.actions[0],
    className: cn(
      MENU_ITEMS.actions[0].className,
      MOBILE_ACTION_PILL,
      'hover:bg-white/10'
    ),
  },
  {
    ...MENU_ITEMS.actions[1],
    className: cn(
      MENU_ITEMS.actions[1].className,
      MOBILE_ACTION_PILL,
      'bg-orangecaixa hover:bg-secondary-hover'
    ),
  },
];

interface HeaderProps {
  type: 'voce' | 'suaempresa' | 'home';
  data: ProductHeaderData | null;
}

export function MobileHeader({ type, data }: HeaderProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lg:hidden fixed w-full z-50">
      <div className="h-[90px] bg-bluecaixa relative overflow-hidden">
        <div className="mx-auto h-full flex items-center justify-between max-w-7xl px-6 xl:px-0">
          <Logo variant={data === null ? 'default' : data.nome} />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Abrir menu de navegação"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-transparent p-0 text-white opacity-100 transition-colors hover:bg-white/10 [&_svg]:size-8"
              >
                <Menu strokeWidth={2} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full bg-bluecaixa p-0 z-50">
              <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
              <ScrollArea className="h-full">
                <div className="flex flex-col min-h-full">
                  <div className="flex px-6 py-10"></div>

                  <div className="flex flex-col items-end space-y-4 p-6 pb-0">
                    {data !== null &&
                      data?.items.map((item) => (
                        <MobileMenuItem key={item.label} item={item} />
                      ))}
                  </div>

                  {data !== null && (
                    <ActionButtons
                      items={[
                        { ...MOBILE_ACTIONS[0], href: data.clienteHref },
                        { ...MOBILE_ACTIONS[1], href: data.adquiraHref },
                      ]}
                      className={MOBILE_ACTIONS_GRID}
                      onClick={() => {
                        setOpen(false);
                      }}
                    />
                  )}

                  <div className="flex flex-col items-end space-y-4 px-6 py-2">
                    {type !== 'home' && (
                      <Logo
                        variant="default"
                        className="h-16 flex items-center"
                      />
                    )}
                    {MENU_ITEMS.main.map((item) => {
                      if (isSimpleMenuItem(item)) {
                        return (
                          <MobileMenuItem
                            key={item.label + item.href}
                            item={{ label: item.label, href: item.href }}
                          />
                        );
                      } else if (isExpandableMenuItem(item))
                        return (
                          <ExpandableMobileMenuItem
                            key={item.label + item.services}
                            item={item}
                          />
                        );
                    })}
                  </div>

                  {type === 'home' && (
                    <ActionButtons
                      items={MOBILE_ACTIONS}
                      className={MOBILE_ACTIONS_GRID}
                      onClick={() => {
                        setOpen(false);
                      }}
                    />
                  )}
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
}

interface MobileMenuItemProps {
  item: {
    href?: string;
    label: string;
  };
}

function MobileMenuItem({ item }: MobileMenuItemProps) {
  const pathname = usePathname();
  const isActive =
    !!item.href &&
    item.href.startsWith('/') &&
    (item.href === '/'
      ? pathname === '/'
      : pathname === item.href || pathname.startsWith(`${item.href}/`));

  return (
    <div className="w-full flex flex-col items-end gap-3">
      <Link
        href={item.href ?? ''}
        className={`text-xs font-bold ${
          isActive ? 'text-orangecaixa' : 'text-white'
        }`}
      >
        {item.label}
      </Link>
      <Separator className="w-full opacity-20" color="#DCDCDC" />
    </div>
  );
}

interface ExpandableMobileMenuItemProps {
  item: ExpandableMenuItem;
}

function ExpandableMobileMenuItem({ item }: ExpandableMobileMenuItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      if (isOpen) {
        content.style.maxHeight = content.scrollHeight + 32 + 'px';
        content.style.opacity = '1';
      } else {
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
      }
    }
  }, [isOpen]);

  return (
    <div className="w-full flex flex-col items-end gap-3">
      <div
        className="w-full flex flex-col items-end gap-3 posicao"
        onClick={toggleOpen}
      >
        <div className="flex flex-row items-center gap-3 text-white text-xs font-bold">
          {item.label}

          <Image
            src={isOpen ? '/icons/chevron-down.png' : '/icons/chevron-up.png'}
            alt="Toggle Chevron"
            height={18}
            width={18}
          />
        </div>
        <Separator className="w-full opacity-20" color="#DCDCDC" />
      </div>

      <div
        className={`w-full flex flex-col gap-3 items-end overflow-hidden transition-all duration-300 ${!isOpen ? 'z-0' : 'z-50'}`}
        ref={contentRef}
      >
        {item.services.map((item) => (
          <div
            key={item.href + item.label}
            className="w-full flex flex-col items-end gap-3"
          >
            <Link
              href={isOpen ? (item.href ?? '') : ''}
              className="text-white text-xs opacity-65 font-bold"
            >
              {item.label}
            </Link>
            <Separator className="w-full opacity-10" color="#DCDCDC" />
          </div>
        ))}
      </div>
    </div>
  );
}
